import { useCallback } from "react";
import { AUTH_STORAGE_KEY } from "../../api/authService/supabase";

const STORAGE_PREFIX = 'bottom-up';

const getUserStorageKey = () => {
    if (typeof window === 'undefined') return STORAGE_PREFIX;
    
    const auth = localStorage.getItem(AUTH_STORAGE_KEY);
    if (auth) {
        const authObj = JSON.parse(auth);
        return `${STORAGE_PREFIX}-${authObj?.user?.id}`;
    }
    return STORAGE_PREFIX;
};

const getUserStorageData = (): Record<string, any> => {
    if (typeof window === 'undefined') return {};
    
    const storageKey = getUserStorageKey();
    const data = localStorage.getItem(storageKey);
    
    if (!data) return {};
    
    try {
        return JSON.parse(data);
    } catch {
        return {};
    }
};

const setUserStorageData = (data: Record<string, any>): void => {
    if (typeof window === 'undefined') return;
    
    const storageKey = getUserStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(data));
};

export const useUserStorage = () => {
    const isLogin = !!localStorage.getItem(AUTH_STORAGE_KEY);
    const get = useCallback(<T = string>(key: string, defaultValue?: T): T | null => {
        if (typeof window === 'undefined') return defaultValue || null;
        
        const data = getUserStorageData();
        const value = data[key];
        
        if (value === undefined) return defaultValue || null;
        
        return value;
    }, []);
    
    const set = useCallback(<T>(key: string, value: T): void => {
        if (typeof window === 'undefined') return;
        
        const data = getUserStorageData();
        data[key] = value;
        setUserStorageData(data);
    }, []);
    
    const remove = useCallback((key: string): void => {
        if (typeof window === 'undefined') return;
        
        const data = getUserStorageData();
        delete data[key];
        setUserStorageData(data);
    }, []);
    
    return {
        get, set, remove, isLogin
    };
};